import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getFleet, getDrivers, sendEngineStop, sendEngineResume } from '../traccar';
import { isAcked, mergeServerAcks, ackHealth } from '../healthAck';
import { assessHealth } from '../vehicleHealth';
import { playOpen, playClose } from '../clickSound';
import VehicleHistory from '../VehicleHistory';
import GoogleMutantLayer from '../GoogleMutantLayer';
import './MapPage.css';

// Inside the native iOS/Android app (Capacitor), Google Maps renders through the web
// layer (GoogleMutant) — the app's origin (capacitor://localhost) is authorized on
// the Google key's website restrictions, so no native plugin is needed. We default
// native to Google satellite. (The @capacitor/google-maps native plugin crashes on
// iOS 26, so it's intentionally NOT used.)
const IS_NATIVE = typeof window !== 'undefined' && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

const ESRI_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const statusColor = (status) => {
  switch (status) {
    case 'online': return '#22c55e';
    case 'unknown': return '#eab308';
    case 'offline': return '#ef4444';
    default: return '#3b82f6';
  }
};

const courseToCardinal = (deg) => {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(((deg % 360) / 45)) % 8];
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// Marker color follows the ignition: green = engine on, red = off
const ignitionColor = (ignition) => (ignition ? '#22c55e' : '#ef4444');

// Classic GPS arrow: a triangle that points where the vehicle is heading
const vehicleIcon = (name, speedMph, ignition, course, driverName, offset, compact) => {
  // Same declutter offset as the native card: shift the label into free space
  // when two cars sit close, so their labels don't stack on top of each other.
  const labelPos = offset
    ? `left:${offset.dx}px;top:${offset.dy}px;`
    : `top:30px;left:50%;transform:translateX(-50%);`;
  const leader = offset
    ? `<svg width="0" height="0" style="position:absolute;left:0;top:0;overflow:visible;pointer-events:none"><line x1="13" y1="13" x2="${offset.dx + 75}" y2="${offset.dy + 30}" stroke="rgba(255,255,255,0.6)" stroke-width="2" stroke-dasharray="3 3"/></svg>`
    : '';
  const count = offset && offset.count > 1 ? offset.count : 0;
  const countBadge = count ? `<div style="position:absolute;top:-7px;right:-9px;min-width:17px;height:17px;padding:0 4px;border-radius:9px;background:#0066ff;color:#fff;font-size:10px;font-weight:900;line-height:17px;text-align:center;border:1.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.55);z-index:5">${count}</div>` : '';
  const arrowSvg = `<svg width="26" height="26" viewBox="0 0 40 40" style="position: relative; transform: rotate(${course || 0}deg); filter: drop-shadow(0 2px 3px rgba(0,0,0,0.6));"><polygon points="20,3 33,35 20,27 7,35" fill="${ignitionColor(ignition)}" stroke="white" stroke-width="2.5" stroke-linejoin="round"/></svg>`;
  // Zoomed out → compact: arrow + small name pill so the spot is clear.
  if (compact) {
    return L.divIcon({
      className: '', iconSize: [26, 26], iconAnchor: [13, 13],
      html: `
        <div style="position:relative">
          <span class="marker-pulse" style="background:${ignitionColor(ignition)};"></span>
          ${leader}
          ${countBadge}
          ${arrowSvg}
          <div style="position:absolute;${labelPos}background:rgba(0,0,0,0.78);color:#fff;padding:2px 8px;border-radius:6px;white-space:nowrap;font-size:11px;font-weight:700;text-align:center;">${name}${Math.round(speedMph) > 0 ? ` · ${Math.round(speedMph)}mph` : ''}</div>
        </div>`,
    });
  }
  return L.divIcon({
    className: '',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    html: `
      <div style="position: relative;">
        <span class="marker-pulse" style="background: ${ignitionColor(ignition)};"></span>
        ${leader}
        ${countBadge}
        <svg width="26" height="26" viewBox="0 0 40 40"
             style="position: relative; transform: rotate(${course || 0}deg); filter: drop-shadow(0 2px 3px rgba(0,0,0,0.6));">
          <polygon points="20,3 33,35 20,27 7,35"
                   fill="${ignitionColor(ignition)}" stroke="white" stroke-width="2.5" stroke-linejoin="round"/>
        </svg>
        <div style="
          position: absolute; ${labelPos}
          background-color: rgba(0,0,0,0.8); color: white; padding: 4px 9px;
          border-radius: 5px; white-space: nowrap; font-size: 12px; text-align: center; line-height: 1.35;
        ">
          <div style="font-weight: 700;">${name} · ${speedMph} mph</div>
          <div style="font-size: 10px; color: ${driverName ? '#7dd3fc' : '#94a3b8'};">
            👤 ${driverName || 'No driver assigned'}
          </div>
        </div>
      </div>
    `,
  });
};

// Battery % from the device's backup battery (null when the tracker has none).
// "You are here" dot — the same visual language as Google/Apple Maps: a blue
// dot with a white ring and a soft pulsing halo.
const meIcon = L.divIcon({
  className: '', iconSize: [22, 22], iconAnchor: [11, 11],
  html: `
    <div style="position:relative;width:22px;height:22px">
      <span style="position:absolute;inset:-9px;border-radius:50%;background:rgba(10,132,255,0.22);animation:ta-me-pulse 2s ease-out infinite"></span>
      <span style="position:absolute;inset:0;border-radius:50%;background:#0a84ff;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.45)"></span>
    </div>`,
});

// Tracker's own battery as a %. Prefer the reported percentage whenever the
// tracker sends one (hardwired units often report 0V but still give a level),
// otherwise estimate it from the cell voltage.
const batteryPctOf = (v) => {
  if (v.batteryLevel != null && v.batteryLevel > 0) return Math.round(v.batteryLevel);
  if (v.deviceBattery != null && v.deviceBattery >= 3.0) {
    return Math.round(Math.min(100, Math.max(0, ((v.deviceBattery - 3.5) / (4.2 - 3.5)) * 100)));
  }
  return null;
};

// The full "always-on" glanceable card next to every car — same rich chips as the
// selected vehicle (power, ignition, battery, speed, signal, sats).
const vehicleRichIcon = (v, driverName, offset, compact) => {
  const ignColor = v.ignition ? '#22c55e' : '#ef4444';
  const chip = (bg, fg, txt) => `<span style="background:${bg};color:${fg};border-radius:5px;padding:2px 6px;font-weight:700;border:1px solid rgba(255,255,255,0.18);text-shadow:0 1px 2px rgba(0,0,0,0.5)">${txt}</span>`;
  const power = v.obd && v.obd.power;
  const batteryPct = batteryPctOf(v);
  const sats = v.sat, rssi = v.rssi;
  const speedMph = v.speedMph || 0;
  // Card placement: default is centered below the arrow. When the declutter pass
  // finds two cards overlapping it hands us an { dx, dy } (pixels from the arrow)
  // that moves this card into free space, so nearby cars never cover each other.
  const cardPos = offset
    ? `left:${offset.dx}px;top:${offset.dy}px;`
    : `top:32px;left:50%;transform:translateX(-50%);`;
  // when the card is pushed off to avoid overlap, draw a thin leader line from
  // the arrow to the card so it's obvious which car the card belongs to
  const leader = offset
    ? `<svg width="0" height="0" style="position:absolute;left:0;top:0;overflow:visible;pointer-events:none"><line x1="13" y1="13" x2="${offset.dx + 75}" y2="${offset.dy + 30}" stroke="rgba(255,255,255,0.55)" stroke-width="2" stroke-dasharray="3 3"/></svg>`
    : '';
  // count bubble when several cars are clustered at this spot
  const count = offset && offset.count > 1 ? offset.count : 0;
  const countBadge = count ? `<div style="position:absolute;top:-7px;right:-9px;min-width:17px;height:17px;padding:0 4px;border-radius:9px;background:#0066ff;color:#fff;font-size:10px;font-weight:900;line-height:17px;text-align:center;border:1.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.55);z-index:5">${count}</div>` : '';
  const arrowSvg = `<svg width="26" height="26" viewBox="0 0 40 40" style="position:relative;transform:rotate(${v.course || 0}deg);filter:drop-shadow(0 2px 3px rgba(0,0,0,.6))"><polygon points="20,3 33,35 20,27 7,35" fill="${ignColor}" stroke="white" stroke-width="2.5" stroke-linejoin="round"/></svg>`;
  // Zoomed out → compact: just the arrow (clearly marking the spot) + a small
  // name pill, so you can see WHERE every car is without big cards blanketing
  // the map. Zoom in for the full stats card below.
  if (compact) {
    return L.divIcon({
      className: '', iconSize: [26, 26], iconAnchor: [13, 13],
      html: `
        <div style="position:relative">
          <span class="marker-pulse" style="background:${ignColor}"></span>
          ${leader}
          ${countBadge}
          ${arrowSvg}
          <div style="position:absolute;${cardPos}background:rgba(9,13,22,0.62);-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.25);border-radius:8px;padding:2px 8px;white-space:nowrap;font-size:11px;font-weight:800;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.9)">${v.name}${Math.round(speedMph) > 0 ? ` · ${Math.round(speedMph)}mph` : ''}</div>
        </div>`,
    });
  }
  return L.divIcon({
    className: '', iconSize: [26, 26], iconAnchor: [13, 13],
    html: `
      <div style="position:relative">
        <span class="marker-pulse" style="background:${ignColor}"></span>
        ${leader}
        ${countBadge}
        <svg width="26" height="26" viewBox="0 0 40 40" style="position:relative;transform:rotate(${v.course || 0}deg);filter:drop-shadow(0 2px 3px rgba(0,0,0,.6))">
          <polygon points="20,3 33,35 20,27 7,35" fill="${ignColor}" stroke="white" stroke-width="2.5" stroke-linejoin="round"/>
        </svg>
        <div style="position:absolute;${cardPos}background:rgba(9,13,22,0.34);-webkit-backdrop-filter:blur(14px) saturate(150%);backdrop-filter:blur(14px) saturate(150%);border:1px solid rgba(255,255,255,0.22);border-radius:12px;padding:6px 9px;white-space:nowrap;box-shadow:0 6px 18px rgba(0,0,0,.35),inset 0 1px 0 rgba(255,255,255,0.14);font-size:11px;line-height:1.55;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.85)">
          <div style="font-weight:800;font-size:12px;margin-bottom:3px;text-shadow:0 1px 4px rgba(0,0,0,0.9)">${v.name}${driverName ? ` · ${driverName}` : ''}</div>
          <div style="display:flex;gap:4px;align-items:center">
            ${chip('rgba(255,255,255,0.14)', '#fff', `⚡ ${power != null ? power.toFixed(1) + 'V' : '—'}`)}
            ${chip(ignColor, '#fff', `🔑 ${v.ignition ? 'ON' : 'OFF'}`)}
            ${batteryPct != null
              ? chip(batteryPct <= 10 ? '#dc2626' : batteryPct <= 50 ? '#f59e0b' : '#16a34a', '#fff', `🔋 ${batteryPct}%`)
              : chip('#475569', '#fff', '🔌 Wired')}
          </div>
          <div style="display:flex;gap:4px;align-items:center;margin-top:2px">
            ${chip('rgba(255,255,255,0.14)', '#fff', `${Math.round(speedMph)} mph`)}
            ${rssi != null ? chip('#16a34a', '#fff', `📶 ${rssi}`) : ''}
            ${sats != null ? chip('#eab308', '#fff', `🛰️ ${sats}`) : ''}
          </div>
        </div>
      </div>
    `,
  });
};

// Keeps the glanceable cards from covering each other. On each settle (and when
// the cars move) it projects every car to screen pixels and, for any card that
// would overlap one already placed, tries a ring of alternate spots (above, to
// the sides, diagonals) until it finds free space. Hands the chosen { dx, dy }
// back up so each car's card renders shifted instead of stacked.
function Declutter({ vehicles, onOffsets }) {
  const map = useMap();
  const raf = useRef(null);
  // recompute only when a car's position actually changes (not every re-render)
  const sig = vehicles.map((v) => `${v.id}:${(v.latitude || 0).toFixed(5)},${(v.longitude || 0).toFixed(5)}`).join('|');
  useEffect(() => {
    const W = 150, H = 60; // approx card footprint
    const spots = [
      { dx: -W / 2, dy: 30 },      // below (default, closest)
      { dx: -W / 2, dy: -H - 6 },  // above
      { dx: 14, dy: -H / 2 },      // right
      { dx: -W - 14, dy: -H / 2 }, // left
      { dx: 14, dy: 22 },          // below-right
      { dx: -W - 14, dy: 22 },     // below-left
      { dx: 14, dy: -H - 6 },      // above-right
      { dx: -W - 14, dy: -H - 6 }, // above-left
    ];
    const overlap = (a, b) => !(a.x2 <= b.x1 || a.x1 >= b.x2 || a.y2 <= b.y1 || a.y1 >= b.y2);
    const CLUSTER_R = 46;    // px: markers this close on screen may overlap
    const SAME_SPOT_M = 150; // meters: only "here together" if the cars are TRULY at the same place (same lot/address), not just close on a zoomed-out map
    const compute = () => {
      // Only fan labels out when zoomed IN enough that a shifted label lands in
      // real free space right by its icon. Zoomed out, markers pack together and
      // a fixed 150px shift dragged the label far from its car — so below street
      // zoom we keep every label pinned under its icon (compact pills already
      // keep the clutter down). Matches the compact-card threshold (zoom 15).
      if (map.getZoom() < 15) { onOffsets({}); return; }
      const pts = vehicles.filter((v) => v.latitude != null).map((v) => {
        const p = map.latLngToContainerPoint([v.latitude, v.longitude]);
        return { id: v.id, x: p.x, y: p.y, lat: v.latitude, lng: v.longitude };
      }).sort((a, b) => a.y - b.y || a.x - b.x);
      // how many cars are ACTUALLY at the same spot as each car (incl. itself) —
      // gated on real-world distance so two cars 15 miles apart don't get a
      // "2" badge just because they're a few pixels apart at low zoom.
      const counts = {};
      pts.forEach((a) => {
        let n = 0;
        pts.forEach((b) => {
          const dx = a.x - b.x, dy = a.y - b.y;
          const closePx = dx * dx + dy * dy <= CLUSTER_R * CLUSTER_R;
          const closeM = map.distance([a.lat, a.lng], [b.lat, b.lng]) <= SAME_SPOT_M;
          if (closePx && closeM) n += 1;
        });
        counts[a.id] = n;
      });
      // A label only needs moving when its MARKER is genuinely stacked on another
      // marker (within CLUSTER_R px). At low zoom the 150px labels overlap even
      // for cars miles apart — shifting those detached the label way off from its
      // icon. So: only cars whose marker is pixel-close to another get a shifted
      // slot; everyone else keeps the clean default label right under the arrow.
      const clustered = new Set();
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y;
          if (dx * dx + dy * dy <= CLUSTER_R * CLUSTER_R) { clustered.add(pts[i].id); clustered.add(pts[j].id); }
        }
      }
      const placed = [];
      const offsets = {};
      // reserve the default label box for EVERY car first, so a shifted label
      // still tries to avoid covering a car that's staying put.
      pts.forEach((c) => {
        if (!clustered.has(c.id)) {
          const s0 = spots[0];
          placed.push({ x1: c.x + s0.dx, y1: c.y + s0.dy, x2: c.x + s0.dx + W, y2: c.y + s0.dy + H });
        }
      });
      pts.forEach((c) => {
        if (!clustered.has(c.id)) return; // isolated car → no offset → clean default under the icon
        let chosen = null;
        for (const s of spots) {
          const box = { x1: c.x + s.dx, y1: c.y + s.dy, x2: c.x + s.dx + W, y2: c.y + s.dy + H };
          if (!placed.some((p) => overlap(box, p))) { chosen = s; placed.push(box); break; }
        }
        if (!chosen) { chosen = spots[0]; placed.push({ x1: c.x + chosen.dx, y1: c.y + chosen.dy, x2: c.x + chosen.dx + W, y2: c.y + chosen.dy + H }); }
        offsets[c.id] = { ...chosen, count: counts[c.id] || 1 };
      });
      onOffsets(offsets);
    };
    const schedule = () => { if (raf.current) cancelAnimationFrame(raf.current); raf.current = requestAnimationFrame(compute); };
    schedule();
    map.on('moveend zoomend', schedule);
    return () => { map.off('moveend zoomend', schedule); if (raf.current) cancelAnimationFrame(raf.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, map]);
  return null;
}

// pans the map to the phone's own location when the "locate me" button is tapped
function CenterOnMe({ trigger, loc }) {
  const map = useMap();
  useEffect(() => {
    if (trigger && loc) map.setView([loc.lat, loc.lng], Math.max(map.getZoom(), 16), { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return null;
}

// First view: fit every car that has a real fix. (Centering on the AVERAGE
// position put the map in the ocean whenever cars were far apart — e.g. the
// midpoint of a car in Miami and one up north is off the coast.)
function InitialCenter({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !points.length) return;
    done.current = true;
    if (points.length === 1) { map.setView(points[0], 13); return; }
    try { map.fitBounds(points, { padding: [60, 80], maxZoom: 15 }); } catch { /* ignore */ }
  }, [points, map]);
  return null;
}

// Smoothly follows the selected vehicle by animating BETWEEN real GPS points
// (never extrapolating past them), so it follows the actual road and turns —
// a couple seconds behind live, but glitch-free. Also draws the route trail.
const FOLLOW_ZOOM = 18;

const bearingBetween = (a, b) => {
  const φ1 = a[0] * Math.PI / 180, φ2 = b[0] * Math.PI / 180, Δλ = (b[1] - a[1]) * Math.PI / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
};

function SmoothVehicle({ vehicle, driverName, onToggle }) {
  const map = useMap();
  const marker = useRef(null);
  const trail = useRef(null);
  const raf = useRef(null);
  const rendered = useRef(null);
  const seg = useRef(null);          // { from, to, t0, dur }
  const prevFixTs = useRef(0);
  const buffer = useRef([]);         // real GPS points → route trail
  const lastFixKey = useRef(null);
  const lastIcon = useRef(0);
  const meta = useRef({ name: '', speedMph: 0, ignition: false, driverName: '' });

  const setMeta = (v) => {
    // Only treat it as a real backup battery if the device reports one. Many OBD
    // trackers (incl. some Teltonika OBD models) have NO internal battery and
    // report 0 V — that must NOT show as "0%" or trigger a low-battery alarm.
    const pct = batteryPctOf(v);
    meta.current = {
      name: v.name, speedMph: v.speedMph || 0, ignition: v.ignition, driverName, course: v.course || 0,
      power: v.obd && v.obd.power, batteryPct: pct, sats: v.sat, rssi: v.rssi,
    };
  };

  // glanceable info card under the arrow
  const makeIcon = (rotation) => {
    const m = meta.current;
    const ignColor = m.ignition ? '#22c55e' : '#ef4444';
    const chip = (bg, fg, txt) => `<span style="background:${bg};color:${fg};border-radius:5px;padding:2px 6px;font-weight:700;border:1px solid rgba(255,255,255,0.18);text-shadow:0 1px 2px rgba(0,0,0,0.5)">${txt}</span>`;
    return L.divIcon({
      className: '', iconSize: [30, 30], iconAnchor: [15, 15],
      html: `
      <div style="position:relative">
        <span class="marker-pulse" style="background:${ignColor}"></span>
        <svg width="30" height="30" viewBox="0 0 40 40" style="position:relative;transform:rotate(${rotation}deg);filter:drop-shadow(0 2px 3px rgba(0,0,0,.6))">
          <polygon points="20,3 33,35 20,27 7,35" fill="${ignColor}" stroke="white" stroke-width="2.5" stroke-linejoin="round"/>
        </svg>
        <div style="position:absolute;top:34px;left:50%;transform:translateX(-50%);background:#fffbe6;border:1px solid #f0cf2e;border-radius:8px;padding:4px 7px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.45);font-size:11px;line-height:1.55;color:#1f2937">
          <div style="font-weight:800;font-size:12px;margin-bottom:2px">${m.name}${m.driverName ? ` · ${m.driverName}` : ''}</div>
          <div style="display:flex;gap:4px;align-items:center">
            ${chip('#111', '#fff', `⚡ ${m.power != null ? m.power.toFixed(1) + 'V' : '—'}`)}
            ${chip(m.ignition ? '#22c55e' : '#ef4444', '#fff', `🔑 ${m.ignition ? 'ON' : 'OFF'}`)}
            ${m.batteryPct != null ? chip(m.batteryPct <= 10 ? '#dc2626' : m.batteryPct <= 50 ? '#f59e0b' : '#16a34a', '#fff', `🔋 ${m.batteryPct}%`) : ''}
          </div>
          <div style="display:flex;gap:4px;align-items:center;margin-top:2px">
            ${chip('#e5e7eb', '#111', `${Math.round(m.speedMph)} mph`)}
            ${m.rssi != null ? chip('#16a34a', '#fff', `📶 ${m.rssi}`) : ''}
            ${m.sats != null ? chip('#eab308', '#fff', `🛰️ ${m.sats}`) : ''}
          </div>
        </div>
      </div>`,
    });
  };

  // when a NEW fix arrives, animate from where we are toward that real point
  useEffect(() => {
    // No live GPS fix (null island / never locked) → Leaflet would throw on a
    // null LatLng, blanking the whole screen. Bail until the tracker locks.
    if (!vehicle || !marker.current || vehicle.latitude == null || vehicle.longitude == null) return;
    setMeta(vehicle);
    // Redraw the bubble on EVERY data update, not just when the car moves.
    // Ignition/battery/stale can change with no new GPS fix (e.g. the engine
    // turns off and the device stops reporting) — the animation loop only
    // repaints the icon while animating between fixes, so without this the
    // bubble would keep showing a stale "ON" until the next fix arrived.
    marker.current.setIcon(makeIcon(meta.current.course || 0));
    const key = vehicle.fixTime || `${vehicle.latitude},${vehicle.longitude}`;
    if (key === lastFixKey.current) return; // same fix, nothing new
    lastFixKey.current = key;
    const fix = [vehicle.latitude, vehicle.longitude];
    const now = performance.now();
    const from = rendered.current || fix;
    // How far the marker would have to travel, and how long since we last drew a
    // fix. A smooth tween only makes sense for a small, live hop between two
    // consecutive polls. When you REOPEN the map (or the tracker had a reporting
    // gap), the newest fix is far from where the marker sat — tweening that made
    // the cursor "fly" all the way across the map to catch up. In that case we
    // SNAP straight to the real location instead.
    const jumpDeg = Math.hypot(fix[0] - from[0], fix[1] - from[1]);
    const gapMs = prevFixTs.current ? now - prevFixTs.current : 0;
    const SNAP_DEG = 0.03; // ~2 mi — larger than any real hop between live polls
    if (!prevFixTs.current || gapMs > 45000 || jumpDeg > SNAP_DEG) {
      // appear immediately where the car actually is
      rendered.current = fix;
      seg.current = null;
      if (marker.current) marker.current.setLatLng(fix);
      map.setView(fix, map.getZoom(), { animate: false });
    } else {
      const dur = Math.min(Math.max(gapMs, 2000), 20000);
      seg.current = { from, to: fix, t0: now, dur };
    }
    prevFixTs.current = now;
  }, [vehicle, driverName]);

  // set up / tear down when the selection changes
  useEffect(() => {
    // Treat a vehicle with no GPS fix like no vehicle: don't create a Leaflet
    // marker at a null LatLng (that throws and crashes Live Tracking). When the
    // tracker locks a fix, `vehicle` updates and this effect re-runs to add it.
    if (!vehicle || vehicle.latitude == null || vehicle.longitude == null
        || !isFinite(vehicle.latitude) || !isFinite(vehicle.longitude)) {
      if (raf.current) cancelAnimationFrame(raf.current);
      if (marker.current) { map.removeLayer(marker.current); marker.current = null; }
      return;
    }
    rendered.current = [vehicle.latitude, vehicle.longitude];
    seg.current = null;
    prevFixTs.current = 0;
    lastFixKey.current = vehicle.fixTime || `${vehicle.latitude},${vehicle.longitude}`;
    setMeta(vehicle);

    marker.current = L.marker(rendered.current, { icon: makeIcon(vehicle.course || 0), zIndexOffset: 1000 }).addTo(map);
    marker.current.on('click', () => onToggle && onToggle());
    map.setView(rendered.current, FOLLOW_ZOOM, { animate: false });

    const loop = () => {
      const s = seg.current;
      if (s) {
        const now = performance.now();
        const frac = Math.min(Math.max((now - s.t0) / s.dur, 0), 1);
        rendered.current = [s.from[0] + (s.to[0] - s.from[0]) * frac, s.from[1] + (s.to[1] - s.from[1]) * frac];
        marker.current.setLatLng(rendered.current);
        const moved = Math.abs(s.to[0] - s.from[0]) + Math.abs(s.to[1] - s.from[1]);
        // moving → point along the path; parked/slow → point the way the car is heading (last course)
        const rotation = (moved > 1e-6 && meta.current.speedMph > 1) ? bearingBetween(s.from, s.to) : meta.current.course;
        if (now - lastIcon.current > 300) {
          marker.current.setIcon(makeIcon(rotation));
          lastIcon.current = now;
        }
        map.setView(rendered.current, map.getZoom(), { animate: false }); // centered follow, no overshoot
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      if (marker.current) { map.removeLayer(marker.current); marker.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle ? vehicle.id : null, map]);

  return null;
}

// Native: tapping anywhere on the map collapses the left drawer.
function MapClickClose({ onClose }) {
  useMapEvents({ click: () => onClose && onClose() });
  return null;
}

// Reports the current zoom up so the markers can switch between the full stats
// card (zoomed in) and a compact name pill (zoomed out).
function ZoomWatch({ onZoom }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => { onZoom(map.getZoom()); /* set initial */ }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// A real GPS fix: numeric and not the 0,0 "no fix yet" placeholder.
const hasFix = (v) => !!v && Number.isFinite(v.latitude) && Number.isFinite(v.longitude)
  && !(Math.abs(v.latitude) < 0.001 && Math.abs(v.longitude) < 0.001);

// Zooms/pans out to fit EVERY car in view when "All cars" is picked (trigger bumps).
function FitAllCars({ trigger, points }) {
  const map = useMap();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; } // skip initial mount
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 15, { animate: true }); return; }
    try { map.fitBounds(points, { padding: [60, 80], animate: true, maxZoom: 16 }); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return null;
}

// Tells Leaflet to redraw when the side panel opens/closes and the map gets more room
function ResizeFix({ trigger }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [trigger, map]);
  return null;
}

// Keeps the map filled whenever its container changes size (banner collapse,
// rotation, drawer) — without this Leaflet keeps its old size and leaves a blank
// (un-tiled) gap when the container grows.
function AutoResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    if (!('ResizeObserver' in window)) return undefined;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

// ------- little gauges like the commercial platforms -------
const SpeedGauge = ({ mph }) => {
  const max = 120;
  const angle = -90 + Math.min(mph, max) / max * 180;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width="90" height="58" viewBox="0 0 100 62">
        <path d="M 10 55 A 40 40 0 0 1 90 55" fill="none" stroke="#e5e7eb" strokeWidth="8" strokeLinecap="round" />
        <path d="M 10 55 A 40 40 0 0 1 50 15" fill="none" stroke="#22c55e" strokeWidth="8" strokeLinecap="round" />
        <path d="M 50 15 A 40 40 0 0 1 78 27" fill="none" stroke="#eab308" strokeWidth="8" strokeLinecap="round" />
        <path d="M 78 27 A 40 40 0 0 1 90 55" fill="none" stroke="#ef4444" strokeWidth="8" strokeLinecap="round" />
        <g transform={`rotate(${angle} 50 55)`}>
          <line x1="50" y1="55" x2="50" y2="22" stroke="#0f172a" strokeWidth="3" strokeLinecap="round" />
        </g>
        <circle cx="50" cy="55" r="5" fill="#0f172a" />
      </svg>
      <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{mph} mph</div>
      <div style={{ fontSize: '11px', color: '#64748b' }}>Speed</div>
    </div>
  );
};

const Compass = ({ course }) => (
  <div style={{ textAlign: 'center' }}>
    <svg width="58" height="58" viewBox="0 0 60 60">
      <circle cx="30" cy="30" r="27" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="2" />
      <text x="30" y="10" textAnchor="middle" fontSize="8" fill="#64748b">N</text>
      <text x="53" y="33" textAnchor="middle" fontSize="8" fill="#64748b">E</text>
      <text x="30" y="57" textAnchor="middle" fontSize="8" fill="#64748b">S</text>
      <text x="7" y="33" textAnchor="middle" fontSize="8" fill="#64748b">W</text>
      <g transform={`rotate(${course} 30 30)`}>
        <polygon points="30,12 34,32 30,28 26,32" fill="#f97316" />
      </g>
      <circle cx="30" cy="30" r="3" fill="#0f172a" />
    </svg>
    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{courseToCardinal(course)} {Math.round(course)}°</div>
    <div style={{ fontSize: '11px', color: '#64748b' }}>Heading</div>
  </div>
);

// ------- info panel building blocks -------
const Section = ({ title }) => (
  <div style={{
    background: 'linear-gradient(90deg, rgba(0, 102, 255, 0.12), rgba(0, 200, 255, 0.04))',
    padding: '6px 10px', fontSize: '12px', fontWeight: 700, color: '#075985',
    borderRadius: '4px', margin: '10px 0 6px', letterSpacing: '0.5px',
    borderLeft: '3px solid #0066ff',
  }}>
    {title}
  </div>
);

const Row = ({ label, value, unit }) =>
  value == null ? null : (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px', padding: '3px 4px', borderBottom: '1px solid #eef2f7' }}>
      <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right', color: '#0f172a' }}>{value}{unit || ''}</span>
    </div>
  );

function MapPage() {
  const [fleet, setFleet] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [, setAckTick] = useState(0); // re-render after "Resolve" on an alarm
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]);
  // default to the free layer to save Google loads, but remember each user's pick
  const [layer, setLayerState] = useState(() => (IS_NATIVE ? (localStorage.getItem('dd-map-layer') || 'gsat') : (localStorage.getItem('dd-map-layer') || 'esri')));
  const setLayer = (k) => { setLayerState(k); localStorage.setItem('dd-map-layer', k); };
  const [panelOpen, setPanelOpen] = useState(!IS_NATIVE); // native starts collapsed → full-screen map
  const [search, setSearch] = useState('');
  const [address, setAddress] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [confirmCut, setConfirmCut] = useState(null); // 'stop' | 'resume' | null
  const [cmdStatus, setCmdStatus] = useState(null);
  const [canScroll, setCanScroll] = useState(false);
  const scrollRef = useRef(null);
  const centeredRef = useRef(false); // only auto-center the map once
  const [historyDevice, setHistoryDevice] = useState(null);
  const [layerMenuOpen, setLayerMenuOpen] = useState(false); // native: floating layer switcher
  const [carPickerOpen, setCarPickerOpen] = useState(false); // native: floating car picker
  const [fitAllReq, setFitAllReq] = useState(0); // bump to fit ALL cars in view
  const [cardOffsets, setCardOffsets] = useState({}); // vehicleId -> {dx,dy} anti-overlap card placement
  const [mapZoom, setMapZoom] = useState(11); // drives compact-vs-full car cards
  const compactCards = mapZoom < 15; // zoomed out → small name pills so positions stay clear
  // the phone's own location, so the owner can see where THEY are versus the car
  const [myLoc, setMyLoc] = useState(null); // { lat, lng, acc }
  const [centerMeReq, setCenterMeReq] = useState(0); // bump to recenter on me
  useEffect(() => {
    let cancelled = false;
    let webWatchId = null;
    let nativeWatchId = null;
    const onPos = (lat, lng, acc) => { if (!cancelled) setMyLoc({ lat, lng, acc }); };

    // On the phone, go through the native Geolocation plugin rather than the
    // browser API. Inside Capacitor the page is served from "localhost", which
    // iOS treats as an ordinary website — so navigator.geolocation re-prompts
    // ("localhost would like to use your current location") on EVERY launch,
    // no matter how many times it's been allowed. The native plugin uses the
    // app's own location permission, which is granted once and remembered.
    (async () => {
      if (IS_NATIVE) {
        try {
          const { Geolocation } = await import('@capacitor/geolocation');
          let perm = await Geolocation.checkPermissions();
          if (perm.location === 'prompt' || perm.location === 'prompt-with-rationale') {
            perm = await Geolocation.requestPermissions();
          }
          if (perm.location !== 'granted') return;
          nativeWatchId = await Geolocation.watchPosition(
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
            (p) => { if (p && p.coords) onPos(p.coords.latitude, p.coords.longitude, p.coords.accuracy); },
          );
          return;
        } catch { /* fall through to the web API */ }
      }
      if (typeof navigator === 'undefined' || !navigator.geolocation) return;
      try {
        webWatchId = navigator.geolocation.watchPosition(
          (p) => onPos(p.coords.latitude, p.coords.longitude, p.coords.accuracy),
          () => { /* denied or unavailable — just don't show the dot */ },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
        );
      } catch { /* ignore */ }
    })();

    return () => {
      cancelled = true;
      try { if (webWatchId != null) navigator.geolocation.clearWatch(webWatchId); } catch { /* ignore */ }
      if (nativeWatchId != null) {
        import('@capacitor/geolocation')
          .then(({ Geolocation }) => Geolocation.clearWatch({ id: nativeWatchId }))
          .catch(() => {});
      }
    };
  }, []);

  // measure whether the details panel can scroll (after content/selection changes)
  // — done in an effect, NOT a callback ref, to avoid a render loop
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) { if (canScroll) setCanScroll(false); return; }
    const more = el.scrollHeight - el.scrollTop - el.clientHeight > 8;
    if (more !== canScroll) setCanScroll(more);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  useEffect(() => {
    let cancelled = false;
    const fetchFleet = async () => {
      try {
        const [vehicles, drs] = await Promise.all([getFleet(), getDrivers().catch(() => [])]);
        if (cancelled) return;
        mergeServerAcks(vehicles); // sync "marked resolved" state from every profile
        setFleet(vehicles);
        setDrivers(drs);
        setError(null);
        // center on the fleet only the FIRST time — after that, don't fight the
        // user's pan/zoom by re-centering every poll (that felt like "jumping").
        if (!centeredRef.current) {
          // Only average cars that actually have coordinates — otherwise a null
          // lat/lng drags the average toward 0,0 and the map flies to the ocean.
          const located = vehicles.filter((v) => Number.isFinite(v.latitude) && Number.isFinite(v.longitude)
            && !(Math.abs(v.latitude) < 0.001 && Math.abs(v.longitude) < 0.001)); // ignore 0,0 (no-fix) so we don't center on the ocean
          if (located.length > 0) {
            const avgLat = located.reduce((s, v) => s + v.latitude, 0) / located.length;
            const avgLng = located.reduce((s, v) => s + v.longitude, 0) / located.length;
            if (Number.isFinite(avgLat) && Number.isFinite(avgLng)) {
              setMapCenter([avgLat, avgLng]);
              centeredRef.current = true;
            }
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
        console.error('Error fetching fleet:', e);
      }
    };
    fetchFleet();
    const interval = setInterval(fetchFleet, 3000); // ~3s refresh for smoother following
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // search filters both the list and the map markers
  const visibleFleet = search.trim()
    ? fleet.filter((v) => {
        const drv = drivers.find((d) => String((d.attributes || {}).currentUnit) === String(v.id));
        const hay = `${v.name} ${drv ? drv.name : ''} ${v.status} ${v.obd.vin || ''} ${v.ignition ? 'on' : 'off'}`.toLowerCase();
        return hay.includes(search.trim().toLowerCase());
      })
    : fleet;

  // auto-select the vehicle passed via ?device= (e.g. from tapping an alert)
  const deviceParamRef = useRef(false);
  useEffect(() => {
    if (deviceParamRef.current || fleet.length === 0) return;
    const devId = new URLSearchParams(window.location.search).get('device');
    if (!devId) { deviceParamRef.current = true; return; }
    const v = fleet.find((x) => String(x.id) === String(devId));
    if (v) { setSelectedId(v.id); setFlyTarget([v.latitude, v.longitude]); deviceParamRef.current = true; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleet]);

  const selected = fleet.find((v) => v.id === selectedId);
  const assignedDriver = selected
    ? drivers.find((d) => String((d.attributes || {}).currentUnit) === String(selected.id))
    : null;

  // Free reverse-geocoding (OpenStreetMap Nominatim) for the selected vehicle
  useEffect(() => {
    if (!selected) { setAddress(null); return; }
    if (selected.latitude == null || selected.longitude == null) { setAddress('No GPS fix yet — waiting for the tracker to lock a location.'); return; }
    let cancelled = false;
    setAddress('Looking up…');
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${selected.latitude}&lon=${selected.longitude}&zoom=17`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setAddress(d.display_name || '—'); })
      .catch(() => { if (!cancelled) setAddress('—'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selected && selected.fixTime]);

  const selectVehicle = (v) => {
    if (v.id === selectedId) {
      // clicking the same vehicle again closes its details
      playClose();
      setSelectedId(null);
      return;
    }
    playOpen();
    setSelectedId(v.id);
    if (v.latitude != null && v.longitude != null) setFlyTarget([v.latitude, v.longitude]);
  };

  return (
    <div className="mappage-container">
      <div className="map-header">
        <h1><span className="live-dot"></span>Live Tracking</h1>
        <span style={{ marginLeft: '14px', fontSize: '12px' }}>
          {error ? `🔴 ${error}` : `Connected — ${fleet.length} vehicle(s)`}
        </span>
        <span style={{ marginLeft: 'auto', marginRight: '200px', display: 'inline-flex', gap: '6px' }}>
          {/* Vehicle picker — same as the app's dropdown: shows the current
              selection, opens a list of "All cars" + every vehicle. Picking one
              flies to it; "All cars" clears the focus and fits the whole fleet. */}
          <span style={{ position: 'relative', display: 'inline-block', marginRight: '6px' }}>
            <button
              onClick={() => setCarPickerOpen((o) => !o)}
              className="layer-toggle-btn"
              title="Pick a vehicle, or view all"
              style={{
                padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: '7px', minWidth: '120px',
              }}
            >
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: selected ? statusColor(selected.status) : '#94a3b8' }} />
              <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected ? ((selected.attributes || {}).displayName || selected.name) : 'All cars'}</span>
              <span style={{ opacity: 0.5 }}>{carPickerOpen ? '▲' : '▼'}</span>
            </button>
            {carPickerOpen && (
              <>
                <div onClick={() => setCarPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1200 }} />
                <div style={{ position: 'absolute', top: '112%', left: 0, minWidth: '210px', maxHeight: '360px', overflowY: 'auto', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 14px 34px rgba(15,23,42,0.2)', zIndex: 1201, padding: '6px' }}>
                  <button
                    onClick={() => { playClose(); setSelectedId(null); setFlyTarget(null); setFitAllReq((n) => n + 1); setCarPickerOpen(false); }}
                    style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', background: !selected ? '#eff6ff' : 'transparent', color: !selected ? '#1d4ed8' : '#0f172a', border: 'none', borderRadius: '8px', padding: '9px 10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    🌐 All cars
                  </button>
                  {fleet.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => { playOpen(); setSelectedId(v.id); if (v.latitude != null && v.longitude != null) setFlyTarget([v.latitude, v.longitude]); setCarPickerOpen(false); }}
                      style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', background: selectedId === v.id ? '#eff6ff' : 'transparent', color: selectedId === v.id ? '#1d4ed8' : '#0f172a', border: 'none', borderRadius: '8px', padding: '9px 10px', fontSize: '13px', fontWeight: selectedId === v.id ? 800 : 600, cursor: 'pointer' }}
                    >
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor(v.status) }} />
                      <span style={{ flex: 1 }}>{(v.attributes || {}).displayName || v.name}</span>
                      {selectedId === v.id && <span>✓</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </span>
          {[['gsat', '🛰️ Satellite HD'], ['groad', '🗺️ Map'], ['esri', '🌍 Standard']].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setLayer(k)}
              className={`layer-toggle-btn ${layer === k ? 'active' : ''}`}
              style={{
                padding: '5px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                border: '1px solid #d1d5db',
                background: layer === k ? 'linear-gradient(135deg, #0066ff, #0047d0)' : 'white',
                color: layer === k ? 'white' : '#374151', fontWeight: 600,
              }}
            >
              {label}
            </button>
          ))}
        </span>
      </div>

      {historyDevice && <VehicleHistory device={historyDevice} onClose={() => { playClose(); setHistoryDevice(null); }} />}

      {/* safety confirmation before any engine command */}
      {confirmCut && selected && (
        <div
          onClick={() => setConfirmCut(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.65)', zIndex: 3000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', maxWidth: '440px', width: '100%', padding: '24px' }}>
            <h3 style={{ margin: '0 0 10px', fontFamily: 'Poppins, sans-serif', fontSize: '17px', color: confirmCut === 'stop' ? '#b91c1c' : '#15803d' }}>
              {confirmCut === 'stop' ? '🔴 Shut off engine?' : '🟢 Restore engine?'}
            </h3>
            <p style={{ fontSize: '14px', color: '#334155', margin: '0 0 6px' }}>
              Vehicle: <b>{selected.name}</b>
              {selected.speedMph > 0 && confirmCut === 'stop' && (
                <span style={{ color: '#b91c1c', fontWeight: 700 }}> — currently moving at {selected.speedMph} mph!</span>
              )}
            </p>
            {confirmCut === 'stop' ? (
              <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px' }}>
                This sends a remote engine-cut command. Only do this when the vehicle is stopped or in a safe
                situation — cutting power to a moving vehicle can cause an accident. Are you sure?
              </p>
            ) : (
              <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px' }}>
                This re-enables the engine so the vehicle can start normally again.
              </p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '9px 18px', borderRadius: '8px', fontWeight: 700 }} onClick={() => setConfirmCut(null)}>
                Cancel
              </button>
              <button
                style={{
                  padding: '9px 18px', borderRadius: '8px', fontWeight: 700, color: 'white',
                  background: confirmCut === 'stop' ? 'linear-gradient(135deg, #dc2626, #991b1b)' : 'linear-gradient(135deg, #059669, #15803d)',
                }}
                onClick={async () => {
                  const action = confirmCut;
                  setConfirmCut(null);
                  setCmdStatus({ ok: true, text: 'Sending command…' });
                  try {
                    await (action === 'stop' ? sendEngineStop(selected.id) : sendEngineResume(selected.id));
                    setCmdStatus({ ok: true, text: action === 'stop' ? '✓ Engine-cut command sent to the device.' : '✓ Engine-restore command sent to the device.' });
                  } catch (e) {
                    setCmdStatus({ ok: false, text: `Command failed — this device may not support engine control (${e.message}).` });
                  }
                }}
              >
                {confirmCut === 'stop' ? 'Yes — shut it off' : 'Yes — restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="map-content" style={{ position: 'relative' }}>
        <div className="map-wrapper" style={{ height: '100%', width: '100%' }}>
          <MapContainer center={mapCenter} zoom={5} style={{ height: '100%', width: '100%' }}>
            {(layer === 'gsat' || layer === 'groad') && (
              <>
                {/* ESRI base UNDER Google. On the phone, Google tiles load on top
                    and cover this. On desktop web the Google key's referrer
                    allowlist doesn't include mytagalong.app, so Google fails and
                    left the map grey — now ESRI satellite shows through instead of
                    a blank map. (Proper fix: add mytagalong.app to the key's
                    allowed HTTP referrers in Google Cloud Console.) */}
                <TileLayer className="sat-bright" url={ESRI_URL} attribution="Tiles &copy; Esri" />
                <GoogleMutantLayer type={layer === 'groad' ? 'roadmap' : 'hybrid'} />
              </>
            )}
            {layer === 'esri' && (
              <>
                <TileLayer className="sat-bright" url={ESRI_URL} attribution="Tiles &copy; Esri" />
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}" />
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
              </>
            )}
            <InitialCenter points={fleet.filter(hasFix).map((v) => [v.latitude, v.longitude])} />
            <AutoResize />
            <FitAllCars trigger={fitAllReq} points={visibleFleet.filter(hasFix).map((v) => [v.latitude, v.longitude])} />
            {IS_NATIVE && <MapClickClose onClose={() => { if (panelOpen) { playClose(); setPanelOpen(false); } }} />}
            <SmoothVehicle
              vehicle={selected || null}
              driverName={assignedDriver ? assignedDriver.name : ''}
              onToggle={() => selected && selectVehicle(selected)}
            />
            <ResizeFix trigger={panelOpen} />
            <CenterOnMe trigger={centerMeReq} loc={myLoc} />
            {/* the phone's own position — accuracy halo + blue dot */}
            {myLoc && (
              <>
                <Circle
                  center={[myLoc.lat, myLoc.lng]}
                  radius={Math.min(Math.max(myLoc.acc || 30, 12), 200)}
                  pathOptions={{ color: '#0a84ff', weight: 1, opacity: 0.35, fillColor: '#0a84ff', fillOpacity: 0.12 }}
                />
                <Marker position={[myLoc.lat, myLoc.lng]} icon={meIcon} zIndexOffset={-500} />
              </>
            )}
            <ZoomWatch onZoom={setMapZoom} />
            <Declutter vehicles={visibleFleet.filter((v) => v.id !== selectedId && v.latitude != null)} onOffsets={setCardOffsets} />
            {visibleFleet.filter((v) => v.id !== selectedId && v.latitude != null).map((v) => (
              <Marker
                key={v.id}
                position={[v.latitude, v.longitude]}
                icon={(() => {
                  const dn = (drivers.find((d) => String((d.attributes || {}).currentUnit) === String(v.id)) || {}).name;
                  // Cards/labels are shifted by the declutter pass so nearby cars
                  // never cover each other. Zoomed out → compact pills so you can
                  // see where each car is without big cards blanketing the map.
                  return IS_NATIVE ? vehicleRichIcon(v, dn, cardOffsets[v.id], compactCards) : vehicleIcon(v.name, v.speedMph, v.ignition, v.course, dn, cardOffsets[v.id], compactCards);
                })()}
                eventHandlers={{ click: () => selectVehicle(v) }}
              />
            ))}
          </MapContainer>
        </div>

        {/* native: floating car picker (quick-select a car, or All) */}
        {IS_NATIVE && (
          <div className="ta-carpick-wrap">
            <button className="ta-carpick-btn" onClick={() => { setCarPickerOpen((o) => !o); setLayerMenuOpen(false); }}>
              <span className="ta-carpick-dot" style={{ background: selected ? statusColor(selected.status) : '#94a3b8' }} />
              <span className="ta-carpick-label">{selected ? ((selected.attributes || {}).displayName || selected.name) : 'All cars'}</span>
              <span style={{ opacity: 0.55, fontSize: '11px' }}>{carPickerOpen ? '▲' : '▼'}</span>
            </button>
            {carPickerOpen && (
              <div className="ta-carpick-menu">
                <button
                  className={`ta-carpick-item ${!selected ? 'on' : ''}`}
                  onClick={() => { playClose(); setSelectedId(null); setCarPickerOpen(false); setFitAllReq((n) => n + 1); }}
                >
                  🌐 <span className="ta-carpick-label">All cars</span>
                </button>
                {fleet.map((v) => (
                  <button
                    key={v.id}
                    className={`ta-carpick-item ${selectedId === v.id ? 'on' : ''}`}
                    onClick={() => { playOpen(); setSelectedId(v.id); if (v.latitude != null && v.longitude != null) setFlyTarget([v.latitude, v.longitude]); setCarPickerOpen(false); }}
                  >
                    <span className="ta-carpick-dot" style={{ background: statusColor(v.status) }} />
                    <span className="ta-carpick-label">{(v.attributes || {}).displayName || v.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* native: floating map-style switcher on the map */}
        {IS_NATIVE && (
          <div className="ta-layers-fab-wrap">
            {layerMenuOpen && (
              <div className="ta-layers-menu">
                {[['gsat', '🛰️ Satellite HD'], ['groad', '🗺️ Map'], ['esri', '🌍 Standard']].map(([k, label]) => (
                  <button
                    key={k}
                    className={`seg-frost ${layer === k ? 'on' : ''}`}
                    onClick={() => { setLayer(k); setLayerMenuOpen(false); }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <button
              className="ta-layers-fab"
              onClick={() => setLayerMenuOpen((o) => !o)}
              title="Map style"
              aria-label="Map style"
            >
              🗺️
            </button>
            {/* jump the map to where the phone is */}
            <button
              className="ta-layers-fab"
              style={{ marginTop: '10px', opacity: myLoc ? 1 : 0.45 }}
              onClick={() => myLoc && setCenterMeReq((n) => n + 1)}
              title={myLoc ? 'Center on my location' : 'Waiting for your location…'}
              aria-label="Center on my location"
            >
              🎯
            </button>
          </div>
        )}

        <div className={`panel-wrap ${panelOpen ? 'open' : 'collapsed'}`} style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
          <button
            className="panel-toggle"
            onClick={() => { (panelOpen ? playClose : playOpen)(); setPanelOpen(!panelOpen); }}
            title={panelOpen ? 'Hide vehicle panel' : 'Show vehicle panel'}
            style={{
              position: 'absolute', top: '50%', left: '-22px', transform: 'translateY(-50%)',
              zIndex: 1100,
              width: '22px', height: '64px', border: 'none', cursor: 'pointer', padding: 0,
              background: '#f97316', color: 'white', fontSize: '15px', fontWeight: 700,
              borderRadius: '8px 0 0 8px', boxShadow: '-2px 2px 8px rgba(15,23,42,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {IS_NATIVE ? (panelOpen ? '▾' : '▴ Vehicles') : (panelOpen ? '›' : '‹')}
          </button>

          {panelOpen && (
          <div className="vehicle-list" style={{ overflow: 'hidden' }}>
          {IS_NATIVE && (
            <div className="ta-drawer-layers">
              {[['gsat', '🛰️ Satellite HD'], ['groad', '🗺️ Map'], ['esri', '🌍 Standard']].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setLayer(k)}
                  className={`seg-frost ${layer === k ? 'on' : ''}`}
                  style={{
                    flex: 1, padding: '7px 4px', borderRadius: '8px', fontSize: '11px', cursor: 'pointer',
                    border: '1px solid #d1d5db', fontWeight: 700,
                    background: layer === k ? 'linear-gradient(135deg, #0066ff, #0047d0)' : 'white',
                    color: layer === k ? 'white' : '#374151',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <h3>Fleet ({fleet.length})</h3>

          <div style={{ padding: '10px 8px 0' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 Search vehicle, driver, VIN…"
              style={{
                width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
                borderRadius: '8px', fontSize: '12px', background: 'white',
              }}
            />
            {search && (
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                {visibleFleet.length} match(es) shown on map ·{' '}
                <span style={{ color: '#1d4ed8', cursor: 'pointer', fontWeight: 700 }} onClick={() => setSearch('')}>clear</span>
              </div>
            )}
          </div>

          {(() => {
            const faults = fleet.filter((v) => (v.obd.dtcCount || 0) > 0 && !isAcked(v.id, 'check-engine', String(v.obd.dtcCount))).length;
            const has = faults > 0;
            // Native: single Fault Alerts card — red on a fault, grey frosted glass when clear.
            if (IS_NATIVE) {
              return (
                <div style={{ padding: '10px 8px 2px' }}>
                  <div className="stat-card" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                    background: has ? 'linear-gradient(135deg, #dc2626, #7f1d1d)' : 'linear-gradient(135deg, rgba(226,232,240,0.55), rgba(148,163,184,0.30))',
                    color: has ? 'white' : '#334155',
                    border: has ? 'none' : '1px solid rgba(255,255,255,0.55)',
                    WebkitBackdropFilter: has ? 'none' : 'blur(14px) saturate(150%)',
                    backdropFilter: has ? 'none' : 'blur(14px) saturate(150%)',
                    boxShadow: has
                      ? 'inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 16px rgba(220,38,38,0.4)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 12px rgba(15,23,42,0.12)',
                  }}>
                    <div className="lbl" style={{ color: has ? 'rgba(255,255,255,0.92)' : '#64748b', fontSize: '11px' }}>
                      {has ? '🔧 Fault Alerts' : '✅ No Fault Alerts'}
                    </div>
                    <div className="num" style={{ color: has ? 'white' : '#334155' }}>{faults}</div>
                  </div>
                </div>
              );
            }
            return (
              <div className="stat-grid">
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #059669, #22c55e)' }}>
                  <div className="num">{fleet.filter((v) => v.motion).length}</div>
                  <div className="lbl">Moving</div>
                </div>
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f97316, #f4511e)' }}>
                  <div className="num">{fleet.filter((v) => !v.motion).length}</div>
                  <div className="lbl">Stopped</div>
                </div>
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #dc2626, #7f1d1d)' }}>
                  <div className="num">{faults}</div>
                  <div className="lbl">Fault Alerts</div>
                </div>
                {(() => {
                  const lowFuel = fleet.filter((v) => v.obd.fuelLevel != null && v.obd.fuelLevel < 15).length;
                  return (
                    <div className="stat-card" style={{ background: lowFuel > 0 ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #00c8ff, #0066ff)' }}>
                      <div className="num">{lowFuel}</div>
                      <div className="lbl">Low Fuel</div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          <div className="vehicle-items" style={selected ? { flex: '0 0 auto', maxHeight: '40%' } : undefined}>
            {visibleFleet.map((v) => (
              <div
                key={v.id}
                className={`vehicle-item ${selectedId === v.id ? 'selected' : ''}`}
                onClick={() => selectVehicle(v)}
              >
                <div
                  className="vehicle-avatar"
                  style={{
                    background: v.ignition
                      ? 'linear-gradient(135deg, #00c8ff, #0066ff)'
                      : 'linear-gradient(135deg, #f97316, #f4511e)',
                  }}
                >
                  {v.name.charAt(0).toUpperCase()}
                </div>
                <div className="vehicle-info">
                  <p className="vehicle-name">{v.name}</p>
                  {(() => {
                    const drv = drivers.find((d) => String((d.attributes || {}).currentUnit) === String(v.id));
                    return (
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: drv ? '#0369a1' : '#94a3b8', fontWeight: drv ? 600 : 400 }}>
                        👤 {drv ? drv.name : 'No driver assigned'}
                      </p>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: '5px', margin: '4px 0' }}>
                    <span style={{
                      fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px',
                      background: v.ignition ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.12)',
                      color: v.ignition ? '#15803d' : '#b91c1c',
                    }}>
                      {v.ignition ? 'ON' : 'OFF'}
                    </span>
                    <span style={{
                      fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px',
                      background: 'rgba(0,102,255,0.10)', color: '#1d4ed8',
                    }}>
                      {v.speedMph} mph
                    </span>
                    {v.noFix && (
                      <span style={{
                        fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px',
                        background: 'rgba(148,163,184,0.18)', color: '#64748b',
                      }} title="The tracker hasn't locked GPS yet — no map location until it does.">
                        📡 No GPS fix
                      </span>
                    )}
                  </div>
                  {v.obd.fuelLevel != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ flex: 1, height: '5px', borderRadius: '999px', background: 'rgba(15,23,42,0.10)' }}>
                        <div style={{
                          width: `${v.obd.fuelLevel}%`, height: '100%', borderRadius: '999px',
                          background: v.obd.fuelLevel < 20
                            ? 'linear-gradient(90deg, #f97316, #ef4444)'
                            : 'linear-gradient(90deg, #00e8ff, #0066ff)',
                        }}></div>
                      </div>
                      <span style={{ fontSize: '10px', color: '#64748b' }}>⛽ {v.obd.fuelLevel}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selected && (
            <div className="ta-details" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, borderTop: '2px solid #e2e8f0' }}>
              {/* scrollable details */}
              <div
                className="ta-details-scroll"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  setCanScroll(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
                }}
                ref={scrollRef}
                style={{ overflowY: 'auto', padding: '4px 4px 0', flex: 1, minHeight: 0, position: 'relative' }}
              >
                {/* active alarms for this vehicle (respects "mark resolved") */}
                {(() => {
                  const attrs = { io30: selected.obd.dtcCount, io32: selected.obd.coolantTemp, io48: selected.obd.fuelLevel, power: selected.obd.power, ignition: selected.ignition, rssi: selected.rssi };
                  const alarms = assessHealth(attrs).filter((c) => (c.level === 'bad' || c.level === 'warn') && !(c.ackKey && isAcked(selected.id, c.ackKey, c.ackSig)));
                  const codes = (selected.dtcCodes || []); // actual DTCs like U3017
                  return (
                    <div style={{ margin: '4px 4px 8px', padding: '10px 12px', borderRadius: '10px', background: alarms.length ? '#fef2f2' : '#f0fdf4', border: `1px solid ${alarms.length ? '#fecaca' : '#bbf7d0'}` }}>
                      <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.5px', color: alarms.length ? '#b91c1c' : '#15803d', marginBottom: alarms.length ? '6px' : 0 }}>
                        {alarms.length ? `🔔 ACTIVE ALARMS (${alarms.length})` : '✅ NO ACTIVE ALARMS'}
                      </div>
                      {alarms.map((c) => (
                        <div key={c.title} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', fontSize: '13px', color: '#334155', padding: '3px 0' }}>
                          <span>
                            {c.icon} <b>{c.title}:</b> {c.verdict}
                            {c.ackKey === 'check-engine' && codes.length > 0 && (
                              <span style={{ display: 'block', fontSize: '12px', color: '#b91c1c', fontWeight: 700, marginTop: '2px' }}>
                                {codes.map((x) => `${x.code} — ${x.meaning}`).join('; ')}
                              </span>
                            )}
                          </span>
                          {c.ackKey && (
                            <button
                              onClick={() => { ackHealth(selected.id, c.ackKey, c.ackSig); setAckTick((n) => n + 1); }}
                              style={{ flexShrink: 0, background: 'white', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '3px 8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                            >
                              ✓ Resolve
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <Section title="ASSET INFORMATION" />
                <Row label="Asset" value={selected.name} />
                <Row label="Driver" value={assignedDriver ? `👤 ${assignedDriver.name} (${assignedDriver.uniqueId})` : 'Unassigned'} />
                <Row label="Status" value={selected.status} />
                <Row label="Ignition" value={selected.ignition == null ? null : selected.stale ? '⏳ Waiting for data' : selected.ignition ? 'On' : 'Off / Stopped'} />
                <Row label="Position" value={selected.latitude != null ? `${selected.latitude.toFixed(6)}, ${selected.longitude.toFixed(6)}` : '⏳ No GPS fix yet'} />
                <Row label="Last report" value={fmtTime(selected.fixTime)} />
                <Row label="Last connection" value={fmtTime(selected.lastUpdate)} />
                <Row label="Address" value={address} />
                <Row label="Odometer" value={selected.obd.odometerKm != null ? `${Math.round(selected.obd.odometerKm * 0.621371).toLocaleString()} mi` : null} />
                <Row label="VIN" value={selected.obd.vin} />

                <Section title="SENSORS" />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px', padding: '3px 4px', borderBottom: '1px solid #eef2f7' }}>
                  <span style={{ color: '#64748b' }}>Speed</span>
                  <span style={{
                    fontWeight: 700,
                    color: selected.speedMaxMph && selected.speedMph >= selected.speedMaxMph ? '#b91c1c'
                      : selected.speedWarnMph && selected.speedMph >= selected.speedWarnMph ? '#b45309' : '#0f172a',
                  }}>
                    {selected.speedMph} mph
                    {selected.speedMaxMph && selected.speedMph >= selected.speedMaxMph ? ' 🚨'
                      : selected.speedWarnMph && selected.speedMph >= selected.speedWarnMph ? ' ⏩' : ''}
                  </span>
                </div>
                <Row label="Speed limits" value={(selected.speedWarnMph || selected.speedMaxMph) ? `warn ${selected.speedWarnMph || '—'} / max ${selected.speedMaxMph || '—'} mph` : 'not set'} />
                <Row label="Direction" value={`${courseToCardinal(selected.course)} ${Math.round(selected.course)}°`} />
                <Row label="Signal" value={selected.rssi != null ? `${'▂▄▆█'.slice(0, Math.min(selected.rssi, 4))} ${selected.rssi}/5` : null} />
                <Row label="Device battery" value={selected.deviceBattery} unit=" V" />
                <Row label="Vehicle battery" value={selected.obd.power} unit=" V" />

                <Section title="ENGINE (OBD)" />
                {selected.ignition !== true && (
                  <div style={{ fontSize: '11px', color: '#94a3b8', padding: '2px 4px 6px' }}>
                    ⚙️ Engine off — live engine readings read 0 until it starts.
                  </div>
                )}
                {/* engine-running-only values read 0 when the engine is off, so a
                    stale "last running" snapshot doesn't look live */}
                <Row label="RPM" value={selected.ignition === true ? selected.obd.rpm : 0} />
                <Row label="Coolant temp" value={selected.ignition === true ? selected.obd.coolantTemp : 0} unit=" °C" />
                <Row label="Engine load" value={selected.ignition === true ? selected.obd.engineLoad : 0} unit="%" />
                <Row label="Throttle" value={selected.ignition === true ? selected.obd.throttle : 0} unit="%" />
                {/* these stay valid with the engine off */}
                <Row label="Fuel level" value={selected.obd.fuelLevel} unit="%" />
                {/* fault count reflects a "mark resolved" done on any tab */}
                <Row label="Fault codes" value={isAcked(selected.id, 'check-engine', String(selected.obd.dtcCount)) ? 0 : selected.obd.dtcCount} />

                <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', padding: '14px 0 6px' }}>
                  <SpeedGauge mph={selected.speedMph} />
                  <Compass course={selected.course} />
                </div>

                {selected.latitude != null && (
                <a
                  href={`https://www.google.com/maps?q=&layer=c&cbll=${selected.latitude},${selected.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'block', textAlign: 'center', margin: '8px 4px 12px', padding: '8px',
                    background: 'linear-gradient(135deg, #f97316, #f4511e)', color: 'white', borderRadius: '6px',
                    fontSize: '13px', fontWeight: 600, textDecoration: 'none',
                  }}
                >
                  🧍 Street View
                </a>
                )}
                <button
                  onClick={() => { playOpen(); setHistoryDevice({ id: selected.id, name: selected.name }); }}
                  style={{
                    display: 'block', width: 'calc(100% - 8px)', textAlign: 'center', margin: '0 4px 12px', padding: '8px',
                    background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white', borderRadius: '6px',
                    fontSize: '13px', fontWeight: 700,
                  }}
                >
                  📅 Trip History
                </button>
              </div>

              {/* scroll-down hint */}
              {canScroll && (
                <div style={{
                  textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#f97316',
                  padding: '3px', background: 'linear-gradient(to top, #f8fafc, rgba(248,250,252,0))',
                  marginTop: '-20px', position: 'relative', pointerEvents: 'none',
                }}>
                  ⌄ scroll for more
                </div>
              )}

              {/* fixed remote-control footer — stays put while the details scroll */}
              <div style={{ borderTop: '1px solid #e2e8f0', background: '#f1f5f9', padding: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', letterSpacing: '0.5px' }}>REMOTE CONTROL</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setConfirmCut('stop')}
                    style={{ flex: 1, padding: '9px', borderRadius: '8px', fontWeight: 700, fontSize: '12px', background: 'linear-gradient(135deg, #dc2626, #991b1b)', color: 'white' }}
                  >
                    🔴 Shut off
                  </button>
                  <button
                    onClick={() => setConfirmCut('resume')}
                    style={{ flex: 1, padding: '9px', borderRadius: '8px', fontWeight: 700, fontSize: '12px', background: 'linear-gradient(135deg, #059669, #15803d)', color: 'white' }}
                  >
                    🟢 Restore
                  </button>
                </div>
                {cmdStatus && (
                  <p style={{ fontSize: '11px', margin: '5px 0 0', color: cmdStatus.ok ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
                    {cmdStatus.text}
                  </p>
                )}
                <p style={{ fontSize: '9px', color: '#94a3b8', margin: '4px 0 0' }}>
                  Requires a wired output relay (not on plug-in OBD trackers).
                </p>
              </div>
            </div>
          )}
        </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MapPage;
